import React, { useEffect, useMemo, useState } from "react";
import {
  FiMapPin,
  FiPhone,
  FiMonitor,
  FiTruck,
  FiShoppingBag,
  FiShare2,
  FiArrowRight,
  FiShoppingCart,
  FiZap,
  FiTag,
  FiClock,
  FiCreditCard,
} from "react-icons/fi";
import {
  FaYoutube,
  FaInstagram,
  FaFacebook,
  FaLinkedin,
  FaTwitter,
} from "react-icons/fa";
import { BadgeCheck, QrCode } from "lucide-react";
import { cdnUrl, cdnUrlForStorageKey } from "../../config/mediaPaths";
import HangingClosedBoard from "../../components/HangingClosedBoard";
import { getCategoryIcon, getCategoryLabel } from "./categoryIcons";
import styles from "./StorefrontPreview.module.css";

const COLLECTION_BG = "https://t3.ftcdn.net/jpg/05/50/25/16/360_F_550251605_51GQTTf3JKEmubgfSKvI38aEpmWODOzl.jpg";

const SOCIAL_META = [
  { key: "youtube", label: "YouTube", Icon: FaYoutube },
  { key: "instagram", label: "Instagram", Icon: FaInstagram },
  { key: "facebook", label: "Facebook", Icon: FaFacebook },
  { key: "linkedin", label: "LinkedIn", Icon: FaLinkedin },
  { key: "x", label: "X", Icon: FaTwitter },
];

function getInitials(name) {
  return (name || "").trim().charAt(0).toUpperCase() || "?";
}

function formatPrice(amount) {
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

function effectivePrice(base, discount) {
  if (!discount || discount <= 0) return base;
  return Math.round(base * (1 - discount / 100));
}

function ItemCard({ item, resolveImg, storefrontOpen = true }) {
  const isProduct = (item?.ItemType || "PRODUCT").toUpperCase() !== "SERVICE";
  const basePrice = Number(item?.BasePrice ?? item?.Price ?? 0);
  const discount = Number(item?.Discount ?? item?.DiscountPercent ?? 0);
  const finalPrice = effectivePrice(basePrice, discount);
  const images = Array.isArray(item?.ItemImages) ? item.ItemImages : [];
  const [imgFailed, setImgFailed] = useState({});
  const videoUrls = Array.isArray(item?.VideoUrls) ? item.VideoUrls.filter(Boolean) : [];
  const unavailable = item?.ItemAvailable === false || item?.ItemAvailable === "false";
  const cannotOrder = unavailable || !storefrontOpen;
  const segmentId = isProduct
    ? (item?.ProductSegment || "")
    : (item?.ServiceSegment || "");
  const segmentLabel = getCategoryLabel(segmentId) || segmentId;

  const urls = images
    .map((img) => resolveImg(img))
    .filter(Boolean);

  return (
    <div className={styles.itemCard}>
      <div className={styles.imageSection}>
        {urls.length > 0 ? (
          <div className={styles.thumbStrip}>
            {urls.map((url, i) => (
              <div key={i} className={styles.thumbWrap}>
                {!imgFailed[i] ? (
                  <img
                    src={url}
                    alt={item?.ItemName || ""}
                    className={styles.thumbImg}
                    onError={() => setImgFailed((prev) => ({ ...prev, [i]: true }))}
                  />
                ) : (
                  <span className={styles.thumbInitials}>{getInitials(item?.ItemName || "")}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.imageWrap}>
            <span className={styles.imageInitials}>{getInitials(item?.ItemName || "")}</span>
          </div>
        )}
        {discount > 0 && (
          <span className={styles.discountBadge}>{discount}% OFF</span>
        )}
        {unavailable && (
          <span className={styles.unavailableBadge}>Unavailable</span>
        )}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardContent}>
          <h3 className={styles.itemTitle}>{item?.ItemName || (isProduct ? "Product" : "Service")}</h3>
          {item?.Description && <p className={styles.itemDesc}>{item.Description}</p>}
          <div>
            <p className={styles.itemPrice}>{formatPrice(finalPrice)}</p>
            {discount > 0 && (
              <div className={styles.priceRow}>
                <span className={styles.originalPrice}>{formatPrice(basePrice)}</span>
                <span className={styles.discountText}>{discount}% OFF</span>
              </div>
            )}
          </div>
          {segmentLabel ? (
            <div className={styles.pillsWrap}>
              <span className={styles.pill}><FiTag size={13} />{segmentLabel}</span>
            </div>
          ) : null}
          {!isProduct && item?.Unit && (
            <div className={styles.pillsWrap}>
              <span className={styles.pill}><FiClock size={13} />Duration: {item.Unit}</span>
            </div>
          )}
          {videoUrls.length > 0 && (
            <div className={styles.videoPillsWrap}>
              {videoUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={styles.videoPill} onClick={(e) => e.stopPropagation()}>
                  ▶ Video {i + 1}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className={styles.cardActions}>
          <div className={styles.btnRow}>
            <button type="button" className={styles.btnPrimary} disabled={cannotOrder}><FiShoppingCart size={17} />Add to Cart</button>
            <button type="button" className={styles.btnOutline} disabled={cannotOrder}><FiZap size={17} />Buy Now</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function readSettings(watchedValues = {}) {
  const nested = watchedValues.BizSettings && typeof watchedValues.BizSettings === "object"
    ? watchedValues.BizSettings
    : {};
  const paymentMethods = Array.isArray(watchedValues.paymentMethods)
    ? watchedValues.paymentMethods
    : (Array.isArray(nested.paymentMethods) ? nested.paymentMethods : ["SELF_MANAGED"]);
  const deliveryOptions = Array.isArray(watchedValues.deliveryOptions)
    ? watchedValues.deliveryOptions
    : (Array.isArray(nested.deliveryOptions) ? nested.deliveryOptions : ["PICKUP"]);
  const socialFootprints = (
    watchedValues.socialFootprints
    && typeof watchedValues.socialFootprints === "object"
    && !Array.isArray(watchedValues.socialFootprints)
  )
    ? watchedValues.socialFootprints
    : (
      nested.socialFootprints
      && typeof nested.socialFootprints === "object"
      && !Array.isArray(nested.socialFootprints)
        ? nested.socialFootprints
        : {}
    );
  const bizPrivate = !!(watchedValues.bizPrivate ?? nested.bizPrivate ?? watchedValues.IsPrivate);
  const bizLive = watchedValues.bizLive ?? nested.bizLive;
  const bizOpenStatus = watchedValues.bizOpenStatus ?? nested.bizOpenStatus;
  return {
    paymentMethods,
    deliveryOptions,
    socialFootprints,
    bizPrivate,
    bizLive,
    bizOpenStatus: bizOpenStatus !== false,
  };
}

export default function StorefrontPreview({ watchedValues = {}, products = [], services = [], resolveImg, hubId, bizId }) {
  const [activeTab, setActiveTab] = useState("products");
  const [heroError, setHeroError] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [previewUnlocked, setPreviewUnlocked] = useState(false);
  const [passkeyInput, setPasskeyInput] = useState("");

  const settings = useMemo(() => readSettings(watchedValues), [watchedValues]);
  const lockedPrivateStore = settings.bizPrivate && !previewUnlocked;

  useEffect(() => {
    setPreviewUnlocked(false);
    setPasskeyInput("");
  }, [settings.bizPrivate, watchedValues?.name, watchedValues?.bizId]);

  useEffect(() => {
    setHeroError(false);
  }, [watchedValues?.bizImageKey]);

  const basenameOnly = (key) => {
    const value = String(key || "");
    const index = value.lastIndexOf("/");
    return index >= 0 ? value.slice(index + 1) : value;
  };

  const storeName = watchedValues?.name || watchedValues?.BizName || "Your Store";
  const storeDescription = watchedValues?.description || watchedValues?.Description || "";
  const bizImageKey = watchedValues?.bizImageKey || watchedValues?.BizImageKey;
  const heroUrl = bizImageKey
    ? (hubId && bizId
        ? cdnUrl({ entity: "biz", fileType: "media", hubId, bizId, fileName: basenameOnly(bizImageKey) })
        : cdnUrlForStorageKey(bizImageKey))
    : null;

  const productSegmentId = watchedValues?.productSegment || products[0]?.ProductSegment || "";
  const serviceSegmentId = watchedValues?.serviceSegment || services[0]?.ServiceSegment || "";
  const productTabLabel = getCategoryLabel(productSegmentId) || "Products";
  const serviceTabLabel = getCategoryLabel(serviceSegmentId) || "Services";
  const ProductTabIcon = getCategoryIcon(productSegmentId);
  const ServiceTabIcon = getCategoryIcon(serviceSegmentId);

  const hasProducts = products.length > 0;
  const hasServices = services.length > 0;
  const showTabs = hasProducts && hasServices;

  useEffect(() => {
    if (showTabs) return;
    if (hasProducts) setActiveTab("products");
    else if (hasServices) setActiveTab("services");
  }, [showTabs, hasProducts, hasServices]);

  const storeLocation = [
    watchedValues?.locality || watchedValues?.Locality,
    watchedValues?.city || watchedValues?.City,
    watchedValues?.state || watchedValues?.State,
  ].filter(Boolean).join(", ")
    || [watchedValues?.address, watchedValues?.Address].filter(Boolean)[0]
    || "";
  const mapUrl = watchedValues?.mapUrl || watchedValues?.MapUrl || "";
  const phone = watchedValues?.phone || watchedValues?.Phone || watchedValues?.BizPhone || "";
  const fullAddress = [
    watchedValues?.address || watchedValues?.Address,
    watchedValues?.locality || watchedValues?.Locality,
    watchedValues?.city || watchedValues?.City,
    watchedValues?.state || watchedValues?.State,
    watchedValues?.zip || watchedValues?.Zip,
    watchedValues?.country || watchedValues?.Country,
  ].filter(Boolean).join(", ");

  const socialLinks = SOCIAL_META
    .map(({ key, label, Icon }) => {
      const url = String(settings.socialFootprints?.[key] || "").trim();
      return url ? { key, label, Icon, url } : null;
    })
    .filter(Boolean);

  const imgResolver = resolveImg || ((img) => (img?.imageKey ? cdnUrlForStorageKey(img.imageKey) : null));

  const sortItems = (items) => {
    if (sortBy === "name-asc") {
      return [...items].sort((a, b) => String(a?.ItemName || "").localeCompare(String(b?.ItemName || "")));
    }
    if (sortBy === "name-desc") {
      return [...items].sort((a, b) => String(b?.ItemName || "").localeCompare(String(a?.ItemName || "")));
    }
    return items;
  };

  const storeOffline = settings.bizLive === false;
  const storefrontOpen = settings.bizOpenStatus !== false;

  return (
    <div className={styles.page}>
      <div className={styles.heroBlock}>
        <div className={styles.heroWrap}>
          {heroUrl && !heroError ? (
            <img src={heroUrl} alt={storeName} className={styles.heroImg} onError={() => setHeroError(true)} />
          ) : (
            <div className={styles.heroFallback}>
              <span className={styles.heroFallbackText}>{getInitials(storeName)}</span>
            </div>
          )}
          <div className={styles.heroOverlay} />

          <div className={styles.logoWrapper}>
            <div className={styles.logoCard}>
              <div className={styles.logoFallback}>
                <span className={styles.logoFallbackZ}>{storeName.charAt(0).toUpperCase()}</span>
                <span className={styles.logoFallbackLabel}>{storeName}</span>
              </div>
            </div>
          </div>

          <div className={styles.qrShareRow}>
            {!lockedPrivateStore && socialLinks.length > 0 && (
              <div className={styles.socialRow}>
                {socialLinks.map(({ key, url, Icon, label }) => (
                  <a
                    key={key}
                    className={styles.socialBtn}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
            <button type="button" className={styles.qrBtn} title="QR code" aria-label="QR code">
              <QrCode size={16} />
            </button>
            <button type="button" className={styles.shareBtnIcon} title="Share" aria-label="Share">
              <FiShare2 size={16} />
            </button>
          </div>

          <div className={styles.heroContent}>
            <div className={styles.storeTitleRow}>
              <h1 className={styles.storeName}>{storeName}</h1>
              <span className={styles.nameVerified} aria-label="Verified">
                <BadgeCheck size={28} fill="currentColor" strokeWidth={2.5} />
              </span>
            </div>
            {storeDescription ? <p className={styles.description}>{storeDescription}</p> : null}
            {!lockedPrivateStore && (mapUrl || storeLocation) ? (
              <div className={styles.statsRow}>
                <span className={styles.statItem}>
                  <FiMapPin size={14} />
                  {mapUrl ? (
                    <a href={mapUrl} target="_blank" rel="noopener noreferrer" className={styles.statLink}>
                      {storeLocation || "View map"}
                    </a>
                  ) : (
                    storeLocation
                  )}
                </span>
              </div>
            ) : null}
          </div>

          <div className={styles.featuresRow}>
            {!lockedPrivateStore && phone ? (
              <span className={styles.featureItem}><FiPhone size={12} />{phone}</span>
            ) : null}
            {!lockedPrivateStore && settings.paymentMethods.includes("SELF_MANAGED") ? (
              <span className={styles.featureItem} title="COD"><FiCreditCard size={12} />COD</span>
            ) : null}
            {!lockedPrivateStore && settings.paymentMethods.includes("GATEWAY") ? (
              <span className={styles.featureItem} title="Online Payment"><FiMonitor size={12} /></span>
            ) : null}
            {!lockedPrivateStore && settings.deliveryOptions.includes("DELIVERY") ? (
              <span className={styles.featureItem}><FiTruck size={12} /><span className={styles.featureLabel}>Delivery Available</span></span>
            ) : null}
            {!lockedPrivateStore && settings.deliveryOptions.includes("PICKUP") ? (
              <span className={styles.featureItem}><FiShoppingBag size={12} /><span className={styles.featureLabel}>Self Pickup</span></span>
            ) : null}
          </div>
        </div>
        {!storefrontOpen ? <HangingClosedBoard label="Closed" hangFromEdge /> : null}
      </div>

      {storeOffline ? (
        <p className={styles.emptyText}>This store is not available right now.</p>
      ) : lockedPrivateStore ? (
        <div className={styles.privateStorePrompt}>
          <div className={styles.privateStoreCard}>
            <h2 className={styles.privateStoreTitle}>Private Store</h2>
            <p className={styles.privateStoreDesc}>
              This store is private. Customers enter the passkey to browse products and services.
            </p>
            <input
              type="password"
              value={passkeyInput}
              onChange={(event) => setPasskeyInput(event.target.value)}
              placeholder="Enter passkey"
              className={styles.passkeyInput}
            />
            <button
              type="button"
              className={styles.unlockBtn}
              onClick={() => setPreviewUnlocked(true)}
            >
              Unlock preview
            </button>
            <p className={styles.previewUnlockHint}>
              Preview unlock does not validate the real passkey — it shows the unlocked customer view.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.collectionBanner} style={{ "--collection-bg": `url(${COLLECTION_BG})` }}>
            <div className={styles.collectionContent}>
              <h2 className={styles.collectionTitle}>Latest Collection</h2>
              <p className={styles.collectionDescription}>Discover our newest arrivals and trending products.</p>
              <button type="button" className={styles.collectionButton}>
                Explore Collection <FiArrowRight size={16} />
              </button>
            </div>
          </div>

          {!hasProducts && !hasServices ? (
            <p className={styles.emptyText}>No products or services added yet.</p>
          ) : (
            <>
              <div className={styles.tabs}>
                <div className={styles.tabGroup}>
                  {showTabs ? (
                    <>
                      <button
                        type="button"
                        className={`${styles.tab} ${activeTab === "products" ? styles.tabActive : styles.tabInactive}`}
                        onClick={() => setActiveTab("products")}
                      >
                        <ProductTabIcon size={14} />
                        {productTabLabel}
                        <span className={styles.tabCount}>({products.length})</span>
                      </button>
                      <button
                        type="button"
                        className={`${styles.tab} ${activeTab === "services" ? styles.tabActive : styles.tabInactive}`}
                        onClick={() => setActiveTab("services")}
                      >
                        <ServiceTabIcon size={14} />
                        {serviceTabLabel}
                        <span className={styles.tabCount}>({services.length})</span>
                      </button>
                    </>
                  ) : hasProducts ? (
                    <span className={`${styles.tab} ${styles.tabActive}`}>
                      <ProductTabIcon size={14} />
                      {productTabLabel}
                      <span className={styles.tabCount}>({products.length})</span>
                    </span>
                  ) : (
                    <span className={`${styles.tab} ${styles.tabActive}`}>
                      <ServiceTabIcon size={14} />
                      {serviceTabLabel}
                      <span className={styles.tabCount}>({services.length})</span>
                    </span>
                  )}
                </div>
                <div className={styles.sortControl}>
                  <label>Sort by:</label>
                  <select className={styles.sortSelect} value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                    <option value="default">Default</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                  </select>
                </div>
              </div>

              {(activeTab === "products" || !showTabs) && hasProducts && (
                <section className={styles.section}>
                  <div className={styles.itemsGrid}>
                    {sortItems(products).map((item, i) => (
                      <ItemCard key={item?.ItemId || i} item={item} resolveImg={imgResolver} storefrontOpen={storefrontOpen} />
                    ))}
                  </div>
                </section>
              )}

              {(activeTab === "services" || !showTabs) && hasServices && (
                <section className={styles.section}>
                  <div className={styles.itemsGrid}>
                    {sortItems(services).map((item, i) => (
                      <ItemCard key={item?.ItemId || i} item={item} resolveImg={imgResolver} storefrontOpen={storefrontOpen} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          <div className={styles.footerWrap}>
            <div className={styles.footerGrid}>
              {fullAddress ? (
                <div className={styles.footerCol}>
                  <div className={styles.footerColTitle}>Address</div>
                  <div className={styles.footerColBody}>{fullAddress}</div>
                </div>
              ) : null}
              {settings.deliveryOptions.includes("DELIVERY") ? (
                <div className={styles.footerCol}>
                  <div className={styles.footerColTitle}>Delivery</div>
                  <div className={styles.footerColBody}>Delivery available</div>
                </div>
              ) : null}
              {settings.deliveryOptions.includes("PICKUP") ? (
                <div className={styles.footerCol}>
                  <div className={styles.footerColTitle}>Pickup</div>
                  <div className={styles.footerColBody}>Self pickup available</div>
                </div>
              ) : null}
              {phone ? (
                <div className={styles.footerCol}>
                  <div className={styles.footerColTitle}>Contact</div>
                  <div className={styles.footerColBody}>{phone}</div>
                </div>
              ) : null}
            </div>
            <div className={styles.footerCopy}>
              ZiniHub&copy; {new Date().getFullYear()} - Powered by ZiniZ. All rights reserved.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
